/**
 * API Client for Cash Position, P&L, and Expense endpoints
 */

import { apiClient, handleApiError } from './apiClient';

export interface CashPositionResponse {
  data: {
    bankBalances: Array<{
      accountId: string;
      name: string;
      code: string;
      balance: number;
      currencyCode: string;
    }>;
    totalCash: number;
    runway: {
      totalCash: number;
      monthlyBurnRate: number;
      monthsRemaining: number;
      status: 'healthy' | 'warning' | 'critical';
      upcomingObligations?: number;
    };
    workingCapital: {
      currentAssets: number;
      currentLiabilities: number;
      workingCapital: number;
      currentRatio: number;
    };
    calculatedAt: string;
  };
}

export interface PnlResponse {
  data: {
    fromDate: string;
    toDate: string;
    revenue: Array<{ accountCode?: string; name: string; amount: number }>;
    totalRevenue: number;
    costOfSales: Array<{ accountCode?: string; name: string; amount: number }>;
    totalCostOfSales: number;
    grossProfit: number;
    expenses: Array<{ accountCode?: string; name: string; amount: number }>;
    totalExpenses: number;
    netProfit: number;
  };
}

export interface ExpenseSummaryResponse {
  data: {
    fromDate: string;
    toDate: string;
    totalExpenses: number;
    categories: Array<{
      accountCode: string;
      accountName: string;
      amount: number;
      count: number;
      percentage: number;
    }>;
    topVendors: Array<{
      contactId: string;
      name: string;
      totalSpend: number;
      invoiceCount: number;
      averageBillAmount: number;
    }>;
  };
}

export async function fetchCashPosition(): Promise<CashPositionResponse['data']> {
  try {
    const response = await apiClient.get<CashPositionResponse>('/cash/position');
    return response.data.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch cash position');
  }
}

export async function fetchPnl(): Promise<PnlResponse['data']> {
  try {
    const response = await apiClient.get<PnlResponse>('/reports/pnl');
    return response.data.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch profit and loss');
  }
}

export async function fetchFyPnl(): Promise<PnlResponse['data']> {
  try {
    const now = new Date();
    const fyYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    const from = `${fyYear}-07-01`;
    const to = now.toISOString().split('T')[0];
    const response = await apiClient.get<PnlResponse>(
      `/reports/pnl?from=${from}&to=${to}`
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch FY profit and loss');
  }
}

export async function fetchExpenses(): Promise<ExpenseSummaryResponse['data']> {
  try {
    const response = await apiClient.get<ExpenseSummaryResponse>('/expenses/summary');
    return response.data.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch expenses');
  }
}
